import React, { useEffect, useState } from "react";
import { useMarketing } from "../../../context/MarketingContext";
import { Label } from "../../../components/ui/Label";
import { Button } from "../../../components/ui/Button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useToast } from "../../../hooks/useToast";
import { Plus, Trash2 } from "lucide-react";
import {
  createCampaignSchedule,
  deleteCampaignSchedule,
  fetchCampaignSchedules,
  formatApiErrors,
  updateCampaignSchedule,
} from "../../../services/marketingTemplatesApi";

const NONE_OPTION = "__none__";
const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

type Frequency =
  | "one_time"
  | "daily"
  | "weekly"
  | "bi_weekly"
  | "monthly"
  | "custom_days";
type TriggerType = "appointment" | "consultation";
type TimingType = "before" | "on_day" | "after";

type ScheduleRule = {
  id: string;
  backendId?: string;
  campaignId: string;
  triggerType: TriggerType;
  frequency: Frequency;
  timing: TimingType;
  daysOffset: number;
  days: string[];
  dayOfMonth: number;
};

const makeRule = (id: number): ScheduleRule => ({
  id: String(id),
  campaignId: NONE_OPTION,
  triggerType: "appointment",
  frequency: "weekly",
  timing: "before",
  daysOffset: 1,
  days: ["Monday"],
  dayOfMonth: 1,
});

const CampaignSchedulingPage: React.FC = () => {
  const toast = useToast();
  const { campaigns, campaignsLoading } = useMarketing();
  const [rules, setRules] = useState<ScheduleRule[]>([makeRule(1)]);
  const [ruleCounter, setRuleCounter] = useState(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingBackendIds, setExistingBackendIds] = useState<string[]>([]);
  const showDaySelection = false;

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const rows = await fetchCampaignSchedules();
        if (!mounted) return;

        if (!rows.length) {
          setRules([makeRule(1)]);
          setRuleCounter(2);
          setExistingBackendIds([]);
          return;
        }

        const mappedRules: ScheduleRule[] = rows.map((row, index) => ({
          id: String(index + 1),
          backendId: String(row.id),
          campaignId: String(row.campaign_id),
          triggerType: row.trigger_type,
          frequency: row.frequency,
          timing: row.timing,
          daysOffset: Number(row.days_offset ?? 1),
          days: Array.isArray(row.days) ? row.days : ["Monday"],
          dayOfMonth: Number(row.day_of_month ?? 1),
        }));

        setRules(mappedRules);
        setRuleCounter(mappedRules.length + 1);
        setExistingBackendIds(rows.map((row) => String(row.id)));
      } catch (err) {
        toast.error(formatApiErrors(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
    };
  }, []);

  const updateRule = (id: string, patch: Partial<ScheduleRule>) => {
    setRules((prev) =>
      prev.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule))
    );
  };

  const toggleRuleDay = (ruleId: string, day: string) => {
    setRules((prev) =>
      prev.map((rule) => {
        if (rule.id !== ruleId) return rule;
        const nextDays = rule.days.includes(day)
          ? rule.days.filter((d) => d !== day)
          : [...rule.days, day];
        return { ...rule, days: nextDays };
      })
    );
  };

  const addRule = () => {
    setRules((prev) => [...prev, makeRule(ruleCounter)]);
    setRuleCounter((prev) => prev + 1);
  };

  const removeRule = (id: string) => {
    setRules((prev) => prev.filter((rule) => rule.id !== id));
  };

  const handleSave = async () => {
    if (!rules.length) {
      toast.error("Please add at least one schedule rule.");
      return;
    }

    for (const rule of rules) {
      if (rule.campaignId === NONE_OPTION) {
        toast.error(`Rule #${rule.id}: please select a campaign.`);
        return;
      }
      if (rule.frequency === "weekly" || rule.frequency === "bi_weekly" || rule.frequency === "custom_days") {
        if (!rule.days.length) {
          toast.error(`Rule #${rule.id}: please select at least one day.`);
          return;
        }
      }
      if (rule.timing !== "on_day" && rule.daysOffset < 1) {
        toast.error(`Rule #${rule.id}: days offset must be 1 or more.`);
        return;
      }
      if (rule.frequency === "monthly" && (rule.dayOfMonth < 1 || rule.dayOfMonth > 31)) {
        toast.error(`Rule #${rule.id}: monthly day must be between 1 and 31.`);
        return;
      }
    }

    try {
      setSaving(true);
      const keptIds: string[] = [];

      for (const rule of rules) {
        const payload = {
          campaign_id: Number(rule.campaignId),
          trigger_type: rule.triggerType,
          frequency: rule.frequency,
          timing: rule.timing,
          days_offset: rule.timing === "on_day" ? 0 : Number(rule.daysOffset),
          day_of_month: rule.frequency === "monthly" ? Number(rule.dayOfMonth) : undefined,
          days:
            rule.frequency === "weekly" ||
            rule.frequency === "bi_weekly" ||
            rule.frequency === "custom_days"
              ? rule.days
              : [],
          is_active: true,
        };

        if (rule.backendId) {
          const updated = await updateCampaignSchedule(rule.backendId, payload);
          keptIds.push(String(updated.id));
        } else {
          const created = await createCampaignSchedule(payload);
          keptIds.push(String(created.id));
        }
      }

      const toDelete = existingBackendIds.filter((id) => !keptIds.includes(id));
      if (toDelete.length) {
        await Promise.all(toDelete.map((id) => deleteCampaignSchedule(id)));
      }

      const refreshed = await fetchCampaignSchedules();
      const refreshedRules: ScheduleRule[] = refreshed.map((row, index) => ({
        id: String(index + 1),
        backendId: String(row.id),
        campaignId: String(row.campaign_id),
        triggerType: row.trigger_type,
        frequency: row.frequency,
        timing: row.timing,
        daysOffset: Number(row.days_offset ?? 1),
        days: Array.isArray(row.days) ? row.days : ["Monday"],
        dayOfMonth: Number(row.day_of_month ?? 1),
      }));

      setRules(refreshedRules.length ? refreshedRules : [makeRule(1)]);
      setRuleCounter((refreshedRules.length || 1) + 1);
      setExistingBackendIds(refreshed.map((row) => String(row.id)));

      toast.success("Campaign scheduling rules saved.");
    } catch (err) {
      toast.error(formatApiErrors(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 lg:p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <p className="text-sm text-gray-500">Loading campaign scheduling rules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base lg:text-lg font-semibold text-gray-900">
            Campaign Scheduling
          </h2>
          <p className="text-xs lg:text-sm text-gray-500">
            Create multiple schedules with before/after day offsets and separate email/SMS templates.
          </p>
        </div>
        <Button
          type="button"
          onClick={addRule}
          className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Add Rule
        </Button>
      </div>

      <div className="max-w-4xl mx-auto space-y-4">
        {rules.map((rule, index) => (
          <div
            key={rule.id}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 space-y-4"
          >
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-900">Schedule Rule #{index + 1}</h3>
              {rules.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => removeRule(rule.id)}
                >
                  <Trash2 className="w-4 h-4 mr-1.5" />
                  Remove
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Campaign</Label>
                <Select
                  value={rule.campaignId}
                  onValueChange={(value) => updateRule(rule.id, { campaignId: value })}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        campaignsLoading
                          ? "Loading campaigns..."
                          : "Select campaign"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_OPTION}>Select campaign</SelectItem>
                    {campaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Trigger Type</Label>
                <Select
                  value={rule.triggerType}
                  onValueChange={(value) =>
                    updateRule(rule.id, { triggerType: value as TriggerType })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select trigger type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="appointment">Appointment</SelectItem>
                    {/* <SelectItem value="consultation">Consultation Form</SelectItem> */}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Frequency</Label>
                <Select
                  value={rule.frequency}
                  onValueChange={(value) =>
                    updateRule(rule.id, { frequency: value as Frequency })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one_time">One Time</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi_weekly">Bi-Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="custom_days">Custom Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Send Timing</Label>
                <Select
                  value={rule.timing}
                  onValueChange={(value) =>
                    updateRule(rule.id, { timing: value as TimingType })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select timing" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="before">Before</SelectItem>
                    <SelectItem value="on_day">On Same Day</SelectItem>
                    <SelectItem value="after">After</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="mr-2">Number of Days</Label>
              <input
                type="number"
                min={0}
                value={rule.daysOffset}
                onChange={(e) =>
                  updateRule(rule.id, { daysOffset: Number(e.target.value || 0) })
                }
                disabled={rule.timing === "on_day"}
                className="w-full md:w-80 rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200 disabled:bg-gray-50 disabled:text-gray-400"
                placeholder="e.g. 2"
              />
              <p className="text-xs text-gray-500">
                {rule.timing === "before" && "Send this many days before the event."}
                {rule.timing === "after" && "Send this many days after the event."}
                {rule.timing === "on_day" && "Offset is disabled for same-day sending."}
              </p>
            </div>

            {showDaySelection && (rule.frequency === "weekly" ||
              rule.frequency === "bi_weekly" ||
              rule.frequency === "custom_days") && (
              <div className="space-y-2">
                <Label>Days Selection</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {DAYS.map((day) => {
                    const active = rule.days.includes(day);
                    return (
                      <button
                        key={`${rule.id}-${day}`}
                        type="button"
                        onClick={() => toggleRuleDay(rule.id, day)}
                        className={`text-sm rounded-md border px-3 py-2 transition ${
                          active
                            ? "bg-[#885ABB] text-white border-[#885ABB]"
                            : "bg-white text-gray-700 border-gray-200 hover:border-[#885ABB]"
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {rule.frequency === "monthly" && (
              <div className="space-y-1.5">
                <Label>Day of Month</Label>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={rule.dayOfMonth}
                  onChange={(e) =>
                    updateRule(rule.id, { dayOfMonth: Number(e.target.value || 1) })
                  }
                  className="w-full md:w-56 rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
                />
              </div>
            )}

          </div>
        ))}

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-[#885ABB] text-white hover:bg-[#6f47a2]"
          >
            {saving ? "Saving..." : "Save Scheduling Rules"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CampaignSchedulingPage;
